
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingActions from '../../components/feature/FloatingActions';
import AnimatedSection from '../../components/base/AnimatedSection';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Questions', icon: 'ri-question-line' },
    { id: 'services', name: 'Services', icon: 'ri-service-line' },
    { id: 'testing', name: 'Testing Process', icon: 'ri-test-tube-line' },
    { id: 'results', name: 'Results & Reports', icon: 'ri-file-list-line' },
    { id: 'billing', name: 'Billing & Insurance', icon: 'ri-money-dollar-circle-line' },
    { id: 'general', name: 'General', icon: 'ri-information-line' },
  ];

  const faqs = [
    {
      category: 'services',
      question: 'What types of laboratory services do you offer?',
      answer: 'We offer comprehensive laboratory services including dental laboratory work (crowns, bridges, implants), medical testing (blood work, pathology, microbiology), clinical diagnostics, genetic testing, research services, and quality control. Our state-of-the-art facility is equipped to handle a wide range of diagnostic and manufacturing needs.',
    },
    {
      category: 'services',
      question: 'Do you provide 24/7 emergency testing services?',
      answer: 'Yes, we offer 24/7 emergency testing services for critical cases. Our dedicated team is available around the clock to process urgent tests and deliver rapid results when time is of the essence. Contact our emergency hotline for immediate assistance.',
    },
    {
      category: 'testing',
      question: 'How long does it take to get test results?',
      answer: 'Turnaround times vary depending on the type of test. Standard blood work typically takes 24-48 hours, while routine tests can be completed within the same day. Complex tests like genetic screening may take 3-5 business days. We also offer express services for urgent cases with results available within hours.',
    },
    {
      category: 'testing',
      question: 'How should I prepare for laboratory tests?',
      answer: 'Preparation requirements vary by test type. Some tests require fasting for 8-12 hours, while others have no special requirements. When you schedule your test, we will provide detailed preparation instructions. Generally, it\'s important to stay hydrated, avoid alcohol 24 hours before testing, and inform us of any medications you\'re taking.',
    },
    {
      category: 'testing',
      question: 'Are your testing procedures safe and accurate?',
      answer: 'Absolutely. We maintain the highest standards of safety and accuracy. Our laboratory is ISO 9001 certified, FDA approved, CAP accredited, and CLIA licensed. We use state-of-the-art equipment, follow strict quality control protocols, and our tests are performed by board-certified professionals. Our accuracy rate exceeds 99.8%.',
    },
    {
      category: 'results',
      question: 'How will I receive my test results?',
      answer: 'Test results are delivered through multiple secure channels based on your preference. You can access results through our secure online portal, receive them via encrypted email, or pick up physical copies at our facility. For abnormal results, our medical team will contact you directly to discuss findings and next steps.',
    },
    {
      category: 'results',
      question: 'Can I get a copy of my test results for my records?',
      answer: 'Yes, you have full access to your test results. You can download digital copies from our patient portal at any time, and we can provide certified printed copies upon request. We maintain your records securely for seven years in compliance with healthcare regulations.',
    },
    {
      category: 'results',
      question: 'What if my test results are abnormal?',
      answer: 'If your results show abnormal values, our medical team will contact you promptly to discuss the findings. We provide detailed explanations of what the results mean and recommend appropriate next steps, which may include follow-up testing, specialist referrals, or treatment options. You\'re never alone in understanding your health status.',
    },
    {
      category: 'billing',
      question: 'Do you accept insurance?',
      answer: 'Yes, we accept most major insurance plans including Medicare and Medicaid. We recommend contacting your insurance provider before testing to verify coverage and understand your out-of-pocket costs. Our billing department can also help verify your benefits and provide cost estimates.',
    },
    {
      category: 'billing',
      question: 'What are your payment options?',
      answer: 'We accept various payment methods including cash, credit cards, debit cards, health savings accounts (HSA), and flexible spending accounts (FSA). We also offer payment plans for larger bills. If you\'re uninsured or underinsured, please speak with our financial counselors about available assistance programs.',
    },
    {
      category: 'billing',
      question: 'How much do laboratory tests cost?',
      answer: 'Test costs vary depending on the type and complexity of the test, as well as your insurance coverage. We provide transparent pricing and can give you a cost estimate before testing. Many routine tests are fully covered by insurance. Contact our billing department for specific pricing information.',
    },
    {
      category: 'general',
      question: 'Do I need a doctor\'s order for laboratory tests?',
      answer: 'For most medical tests, yes, you need a physician\'s order. However, we offer some wellness and screening tests that don\'t require a doctor\'s order. Contact us to discuss your specific testing needs, and we can guide you on whether a physician\'s order is required.',
    },
    {
      category: 'general',
      question: 'What are your operating hours?',
      answer: 'Our main laboratory is open Monday through Friday from 8:00 AM to 6:00 PM, and Saturday from 9:00 AM to 2:00 PM. We\'re closed on Sundays and major holidays. However, our emergency testing services are available 24/7 for urgent cases. Specimen drop-off is available during extended hours.',
    },
    {
      category: 'general',
      question: 'How do I schedule an appointment?',
      answer: 'You can schedule an appointment through our website, by calling our office, or via our mobile app. Walk-ins are also welcome, though appointments receive priority service. For specialized tests, we recommend scheduling in advance to ensure proper preparation and availability.',
    },
    {
      category: 'general',
      question: 'Are your facilities accessible for people with disabilities?',
      answer: 'Yes, all our facilities are fully ADA compliant and wheelchair accessible. We have accessible parking, ramps, elevators, and restrooms. Our staff is trained to assist patients with special needs. Please let us know if you require any specific accommodations when scheduling your appointment.',
    },
    {
      category: 'services',
      question: 'Do you offer mobile or at-home testing services?',
      answer: 'Yes, we offer mobile phlebotomy services for patients who cannot visit our facility due to mobility issues, illness, or other circumstances. Our certified phlebotomists can come to your home, office, or care facility to collect samples. Contact us to schedule a mobile service appointment.',
    },
  ];

  const filteredFaqs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingActions />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/578a5aa3eddd61bcf6ab7585b28a2de2.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-royal-900/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl lg:text-6xl font-display font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl mb-6 max-w-2xl mx-auto">
            Find answers to common questions about our services and processes
          </p>
          <div className="flex items-center justify-center space-x-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>FAQ</span>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Category Filter */}
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'bg-primary-500 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                  }`}
                >
                  <i className={`${category.icon} text-lg`}></i>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* FAQ List */}
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h2 className="text-2xl font-display font-bold text-gray-900">
                {filteredFaqs.length} Question{filteredFaqs.length !== 1 ? 's' : ''} Found
              </h2>
            </div>

            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <AnimatedSection key={index} delay={index * 0.05}>
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-50 transition-colors duration-300"
                    >
                      <div className="flex items-start space-x-4 flex-1 pr-4">
                        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-primary-100 rounded-xl">
                          <i className="ri-question-line text-primary-600 text-xl"></i>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 flex-1">
                          {faq.question}
                        </h3>
                      </div>
                      <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 bg-primary-100 rounded-lg transition-transform duration-300 ${
                        openIndex === index ? 'rotate-45' : ''
                      }`}>
                        <i className="ri-add-line text-primary-600 text-xl"></i>
                      </div>
                    </button>
                    
                    {openIndex === index && (
                      <div className="px-6 pb-6">
                        <div className="pl-14">
                          <p className="text-gray-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <AnimatedSection className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="bg-gradient-to-br from-primary-50 to-cyan-50 rounded-3xl p-12">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-primary-500 rounded-2xl">
              <i className="ri-customer-service-2-line text-white text-4xl"></i>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-gray-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Our team is here to help. Contact us for personalized assistance.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="px-10 py-4 bg-gradient-to-r from-primary-500 to-royal-600 text-white rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                Contact Support
              </Link>
              <a
                href="tel:+1234567890"
                className="px-10 py-4 bg-white border-2 border-primary-500 text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-all duration-300 whitespace-nowrap"
              >
                Call Us Now
              </a>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </div>
  );
};

export default FAQPage;
